import { useState, useEffect, useCallback, Suspense, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Eye, Save, Loader2, Palette, CheckCircle2, XCircle } from "lucide-react";
import type { JoinThemeConfig, JoinPreset } from "@wedding/shared";
import type { ThemeEditorProps } from "./types";
import { PresetGrid } from "./themes/PresetGrid";
import { PaletteSelector } from "./themes/PaletteSelector";
import { FontPairSelector } from "./themes/FontPairSelector";
import { ImageUploader } from "./themes/ImageUploader";
import { VideoFrameExtractor } from "./themes/VideoFrameExtractor";
import { LayoutPicker } from "./themes/LayoutPicker";
import { getOrganizerLayout } from "./themes/layout-registry";

// ─── Local toast ──────────────────────────────────────────────────────────────

function Toast({ msg, ok, onDone }: { msg: string; ok: boolean; onDone: () => void }) {
  useEffect(() => {
    const id = setTimeout(onDone, 2800);
    return () => clearTimeout(id);
  }, [onDone]);
  return (
    <div
      className={[
        "fixed bottom-24 left-1/2 -translate-x-1/2 z-[200] md:bottom-8",
        "flex items-center gap-2.5 px-4 py-3 rounded-2xl shadow-xl text-sm font-medium",
        "animate-in slide-in-from-bottom-4 fade-in duration-300",
        ok ? "bg-emerald-500 text-white" : "bg-red-500 text-white",
      ].join(" ")}
    >
      {ok ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <XCircle className="h-4 w-4 shrink-0" />}
      {msg}
    </div>
  );
}

// ─── Deep merge for object defaults + stored config ───────────────────────────

function deepMerge<T>(base: T, override: unknown): T {
  if (override === null || override === undefined) return base;
  if (typeof base !== "object" || base === null || Array.isArray(base)) return override as T;
  if (typeof override !== "object" || Array.isArray(override)) return override as T;
  const result: Record<string, unknown> = { ...(base as Record<string, unknown>) };
  for (const [k, v] of Object.entries(override as Record<string, unknown>)) {
    result[k] = deepMerge(result[k], v);
  }
  return result as T;
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function ThemeEditor({ slug, wedding, refresh, api }: ThemeEditorProps) {
  const { t } = useTranslation();
  const th = (key: string) => t(`themes.${key}`);

  const [localTheme, setLocalTheme] = useState<JoinThemeConfig>({});
  const [heroUrl, setHeroUrl] = useState<string | null>(null);
  const [galleryUrls, setGalleryUrls] = useState<string[]>([]);
  const [frameCount, setFrameCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const showToast = useCallback((msg: string, ok = true) => setToast({ msg, ok }), []);
  const dismissToast = useCallback(() => setToast(null), []);

  useEffect(() => {
    window.scrollTo(0, 0);
    const el = document.getElementById("content-scroll");
    if (el) el.scrollTop = 0;
  }, []);

  useEffect(() => {
    if (!wedding) return;
    const join = (wedding.config?.join ?? {}) as JoinThemeConfig;
    setLocalTheme(join);
    setHeroUrl(wedding.cover_photo_url ?? null);
    setGalleryUrls(wedding.join_cover_images ?? []);
    setFrameCount(wedding.join_frame_count ?? 0);
    setDirty(false);
  }, [wedding]);

  // beforeunload dirty warning
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  // Active layout — defaults to "default" when no layoutId is set.
  const activeLayoutId = localTheme.layoutId ?? "default";
  const activeLayout = useMemo(() => getOrganizerLayout(activeLayoutId), [activeLayoutId]);

  // Merge stored config with layout defaults so the editor sees a fully-shaped object.
  const editorValue = useMemo(() => {
    if (!activeLayout?.defaults) return undefined;
    return deepMerge(activeLayout.defaults, localTheme.config ?? {});
  }, [activeLayout, localTheme.config]);

  // ── State updates ─────────────────────────────────────────────────────
  function updateTheme(patch: Partial<JoinThemeConfig>) {
    setLocalTheme((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  }

  function handleLayoutChange(id: string) {
    if (id === activeLayoutId) return;
    setLocalTheme((prev) => ({
      ...prev,
      layoutId: id,
      // Wipe layout-specific config when switching layouts — schemas differ.
      config: id === "default" ? prev.config : {},
    }));
    setDirty(true);
  }

  function handleLayoutConfigChange(next: unknown) {
    setLocalTheme((prev) => ({
      ...prev,
      config: next as Record<string, unknown>,
    }));
    setDirty(true);
  }

  function handlePresetChange(preset: JoinPreset) {
    const defaultPalette: Record<JoinPreset, string> = {
      romantic: "blush-gold",
      modern: "modern-slate",
      minimal: "warm-ivory",
      boho: "terracotta",
      "dark-romance": "dark-wine",
    };
    setLocalTheme((prev) => ({
      ...prev,
      preset,
      palette: defaultPalette[preset] ?? "warm-ivory",
      fontPair: "elegant",
    }));
    setDirty(true);
  }

  // ── Save ──────────────────────────────────────────────────────────────
  async function handleSave() {
    setSaving(true);
    try {
      // Validate layout config if a schema exists for the active layout.
      if (activeLayout?.schema && editorValue !== undefined) {
        const parsed = activeLayout.schema.safeParse(editorValue);
        if (!parsed.success) {
          const first = parsed.error.errors[0];
          const where = first?.path?.length ? ` (${first.path.join(".")})` : "";
          showToast((first?.message ?? th("saveError")) + where, false);
          setSaving(false);
          return;
        }
      }

      await api.updateConfig({
        layoutId: activeLayoutId,
        config: activeLayout?.editor ? (editorValue as Record<string, unknown>) : undefined,
        preset: localTheme.preset,
        palette: localTheme.palette,
        fontPair: localTheme.fontPair,
      });
      await refresh();
      setDirty(false);
      showToast(th("saveSuccess"));
    } catch (e) {
      console.error("Theme save failed:", e);
      showToast(th("saveError"), false);
    } finally {
      setSaving(false);
    }
  }

  function handlePreview() {
    // Save-then-preview: open the public join page in a new tab.
    // (Live postMessage iframe is intentionally not implemented yet — Plan §6.)
    const url = `/${slug}/join`;
    // Map the current panel host → the guest host (works from organizer OR admin,
    // prod or staging) so the preview opens the public join page.
    const guestHost = window.location.host
      .replace(/^stagingorga\.|^stagingadmin\./, "stagingweb.")
      .replace(/^organizer\.|^admin\./, "");
    const target = wedding ? `https://${guestHost}${url}` : url;
    window.open(target, "_blank", "noopener,noreferrer");
  }

  // ── Image / frame handlers (unchanged) ────────────────────────────────
  async function handleHeroUpload(file: File) {
    try { const { url } = await api.uploadHero(file); setHeroUrl(url); setDirty(true); showToast(th("saveSuccess")); }
    catch (e) { console.error(e); showToast(th("uploadError"), false); }
  }
  async function handleHeroDelete() {
    try { await api.deleteHero(); setHeroUrl(null); setDirty(true); }
    catch (e) { console.error(e); showToast(th("deleteError"), false); }
  }
  async function handleGalleryAdd(file: File) {
    try { const { url } = await api.addGalleryImage(file); setGalleryUrls((prev) => [...prev, url]); setDirty(true); showToast(th("saveSuccess")); }
    catch (e) { console.error(e); showToast(th("uploadError"), false); }
  }
  async function handleGalleryDelete(index: number) {
    try { await api.deleteGalleryImage(index); setGalleryUrls((prev) => prev.filter((_, i) => i !== index)); setDirty(true); }
    catch (e) { console.error(e); showToast(th("deleteError"), false); }
  }
  async function handleFramesUploaded(count: number) {
    try { await api.setFrameCount(count); setFrameCount(count); showToast(th("videoSuccess")); }
    catch (e) { console.error(e); showToast(th("videoError"), false); }
  }
  async function handleDeleteFrames() {
    try { await api.deleteFrames(); setFrameCount(0); }
    catch (e) { console.error(e); showToast(th("deleteError"), false); }
  }

  if (!wedding) return null;

  const Editor = activeLayout?.editor;

  return (
    <>
      <div className="max-w-4xl mx-auto pb-32 space-y-8">
        {/* ── Page header ─────────────────────────────── */}
        <div className="flex items-start justify-between gap-4 pt-2">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary/10">
              <Palette className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold">{th("joinTitle")}</h1>
              <p className="mt-0.5 text-sm text-muted-foreground">{th("joinSubtitle")}</p>
            </div>
          </div>
          {/* Desktop Save/Preview — mobile uses sticky bottom bar below */}
          <div className="hidden sm:flex shrink-0 gap-2">
            <button
              onClick={handlePreview}
              className="flex items-center gap-1.5 rounded-xl border border-border px-4 py-2 text-sm font-medium hover:bg-muted transition-colors cursor-pointer"
            >
              <Eye className="h-4 w-4" />
              {th("preview")}
            </button>
            <button
              onClick={handleSave}
              disabled={!dirty || saving}
              className="flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50 transition-colors cursor-pointer"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? t("common.saving") : t("common.save")}
            </button>
          </div>
        </div>

        {/* ── Layout picker ───────────────────────────── */}
        <section className="space-y-3">
          <div>
            <h2 className="text-base font-semibold">{th("layoutPickerTitle")}</h2>
            <p className="mt-0.5 text-sm text-muted-foreground">{th("layoutPickerSubtitle")}</p>
          </div>
          <LayoutPicker selected={activeLayoutId} onChange={handleLayoutChange} />
        </section>

        {/* ── Layout-specific editor (lazy) ───────────── */}
        {Editor && editorValue !== undefined && (
          <section className="space-y-3">
            <div className="border-t border-border pt-6">
              <h2 className="text-base font-semibold">{th("layoutEditorTitle")}</h2>
              <p className="mt-0.5 text-sm text-muted-foreground">{th("layoutEditorSubtitle")}</p>
            </div>
            <Suspense
              fallback={
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              }
            >
              <Editor
                value={editorValue}
                onChange={handleLayoutConfigChange}
                slug={slug}
                galleryUrls={galleryUrls}
                uploader={{
                  upload: api.uploadAsset,
                  remove: (key) => api.deleteAsset(key).then(() => {}),
                }}
              />
            </Suspense>
          </section>
        )}

        {/* ── Legacy preset/palette/font UI — only for default layout ── */}
        {activeLayoutId === "default" && (
          <>
            <section className="space-y-3">
              <h2 className="text-base font-semibold">{th("presets")}</h2>
              <PresetGrid selected={localTheme.preset} onChange={handlePresetChange} />
            </section>

            <section className="space-y-6">
              <div className="border-t border-border pt-6">
                <h2 className="text-base font-semibold">{th("customize")}</h2>
              </div>

              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{th("palette")}</h3>
                <PaletteSelector selected={localTheme.palette} onChange={(p) => updateTheme({ palette: p })} />
              </div>

              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{th("fontPair")}</h3>
                <FontPairSelector selected={localTheme.fontPair} onChange={(f) => updateTheme({ fontPair: f })} />
              </div>
            </section>
          </>
        )}

        {/* ── Image management — applies to all layouts ── */}
        <section className="space-y-3">
          <div className="border-t border-border pt-6">
            <h2 className="text-base font-semibold">{th("images")}</h2>
          </div>
          {activeLayoutId === "default" && (
            <VideoFrameExtractor
              frameCount={frameCount}
              onFramesUploaded={handleFramesUploaded}
              onDelete={handleDeleteFrames}
              uploadFrame={(blob, index) => api.uploadFrame(blob, index).then(() => {})}
            />
          )}
          <ImageUploader
            heroUrl={heroUrl}
            galleryUrls={galleryUrls}
            onHeroUpload={handleHeroUpload}
            onHeroDelete={handleHeroDelete}
            onGalleryAdd={handleGalleryAdd}
            onGalleryDelete={handleGalleryDelete}
          />
        </section>
      </div>

      {/* ── Mobile sticky bottom Save/Preview bar ─── */}
      <div
        className="sm:hidden fixed bottom-16 left-0 right-0 z-40 border-t border-border bg-background/95 backdrop-blur px-4 py-3 flex gap-2"
      >
        <button
          onClick={handlePreview}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-border px-4 py-3 text-sm font-medium hover:bg-muted transition-colors cursor-pointer"
        >
          <Eye className="h-4 w-4" />
          {th("preview")}
        </button>
        <button
          onClick={handleSave}
          disabled={!dirty || saving}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50 transition-colors cursor-pointer"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {saving ? t("common.saving") : t("common.save")}
        </button>
      </div>

      {toast && <Toast msg={toast.msg} ok={toast.ok} onDone={dismissToast} />}
    </>
  );
}
